<template>
  <div class="config-editor">
    <div class="editor-tabs">
      <el-radio-group v-model="editorMode" size="small">
        <el-radio-button label="visual">可视化编辑</el-radio-button>
        <el-radio-button label="json">JSON编辑</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 可视化编辑器 -->
    <div v-show="editorMode === 'visual'" class="visual-editor">
      <el-collapse v-model="activeGroups" accordion>
        <el-collapse-item
          v-for="(group, groupIndex) in configForm"
          :key="groupIndex"
          :title="group.groupTitle || '配置项'"
          :name="groupIndex"
        >
          <div
            v-for="(item, itemIndex) in group.items"
            :key="itemIndex"
            :class="['config-item', { 'config-item-disabled': !item.enable }]"
          >
            <div class="config-item-header">
              <el-checkbox
                v-model="item.enable"
                @change="handleItemToggle(item)"
              >
                <span class="config-label">{{ item.label }}</span>
              </el-checkbox>
            </div>

            <div v-if="item.enable" class="config-item-body">
              <!-- Boolean 类型 -->
              <div v-if="item.type === 'boolean'" class="config-control">
                <el-switch
                  v-model="item.value"
                  @change="updateConfig"
                  active-text="开启"
                  inactive-text="关闭"
                ></el-switch>
              </div>

              <!-- Number 类型 -->
              <div v-else-if="item.type === 'number'" class="config-control">
                <el-slider
                  v-if="item.range && Array.isArray(item.range)"
                  v-model="item.value"
                  @change="updateConfig"
                  :min="item.range[0]"
                  :max="item.range[1]"
                  :step="item.step || 1"
                  show-input
                  :input-size="'small'"
                ></el-slider>
                <el-input-number
                  v-else
                  v-model="item.value"
                  @change="updateConfig"
                  :step="item.step || 1"
                  size="small"
                ></el-input-number>
              </div>

              <!-- Select 类型 -->
              <div v-else-if="item.type === 'select'" class="config-control">
                <el-select
                  v-model="item.value"
                  @change="handleSelectChange(item)"
                  placeholder="请选择"
                  style="width: 100%"
                >
                  <el-option
                    v-for="option in item.range"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  ></el-option>
                </el-select>
              </div>

              <div class="config-key">
                <span class="key-label">配置键:</span>
                <code>{{ item.key }}</code>
              </div>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>

    <!-- JSON 编辑器 -->
    <div v-show="editorMode === 'json'" class="json-editor">
      <el-input
        type="textarea"
        v-model="jsonString"
        @input="handleJsonChange"
        :rows="20"
        placeholder="请输入配置内容（JSON格式）"
      ></el-input>
      <div v-if="jsonError" class="json-error">{{ jsonError }}</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ConfigEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      editorMode: "visual",
      activeGroups: [0],
      jsonString: "",
      jsonError: "",
      configForm: [
        {
          groupTitle: "基础配置",
          items: [
            {
              key: "dpiConfig.dpi",
              label: "DPI设置",
              enable: false,
              value: 350,
              range: [200, 640],
              type: "number",
            },
            {
              key: "dpiConfig.enable",
              label: "APP原始DPI（关闭DPI配置）",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "extraConfig.allowSplashScreen",
              label: "移除启动动画",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "extraConfig.forceFullScreen",
              label: "强制全屏",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "extraConfig.fullPMAccess",
              label: "后台持续运行",
              enable: false,
              type: "boolean",
              value: false,
            },
          ],
        },
        {
          groupTitle: "显示配置",
          items: [
            {
              key: "globalActivityConfig.displayConfig.enhance",
              label: "显示增强",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "globalActivityConfig.displayConfig.ditherThreshold",
              label: "高对比度",
              enable: false,
              value: 255,
              range: [128, 255],
              type: "number",
            },
            {
              key: "globalActivityConfig.displayConfig.contrast",
              label: "深色增强",
              enable: false,
              value: 0,
              range: [0, 100],
              type: "number",
            },
            {
              key: "globalActivityConfig.displayConfig.monoLevel",
              label: "浅色过滤",
              enable: false,
              value: 0,
              range: [0, 100],
              type: "number",
            },
            {
              key: "globalActivityConfig.displayConfig.cfaColorBrightness",
              label: "色彩亮度",
              enable: false,
              value: 0,
              range: [0, 100],
              type: "number",
            },
            {
              key: "globalActivityConfig.displayConfig.cfaColorSaturation",
              label: "鲜艳度增强",
              enable: false,
              value: 0,
              range: [0, 100],
              type: "number",
            },
          ],
        },
        {
          groupTitle: "手写与绘图",
          items: [
            {
              key: "globalActivityConfig.noteConfig.enable",
              label: "手写优化",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "globalActivityConfig.noteConfig.globalStrokeStyle.strokeWidth",
              label: "线宽",
              enable: false,
              value: 3,
              range: [0.5, 20],
              step: 0.5,
              type: "number",
            },
            {
              key: "globalActivityConfig.paintConfig.antiAlisingType",
              label: "消除字体锯齿",
              enable: false,
              value: 0,
              range: [0, 100],
              type: "number",
            },
            {
              key: "globalActivityConfig.paintConfig.fillContrast",
              label: "背景颜色",
              enable: false,
              value: 1,
              range: [0, 255],
              type: "number",
            },
            {
              key: "globalActivityConfig.paintConfig.iconContrast",
              label: "图标颜色",
              enable: false,
              value: 1,
              range: [0, 255],
              type: "number",
            },
            {
              key: "globalActivityConfig.paintConfig.imgEAC",
              label: "封面颜色",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "globalActivityConfig.paintConfig.textBold",
              label: "标题字体加粗",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "globalActivityConfig.paintConfig.textEACType",
              label: "文字描边",
              enable: false,
              value: 0,
              range: [0, 3],
              type: "number",
            },
          ],
        },
        {
          groupTitle: "刷新配置",
          items: [
            {
              key: "globalActivityConfig.refreshConfig.animationDuration",
              label: "动画过滤时间",
              enable: false,
              value: 50,
              range: [0, 3000],
              type: "number",
            },
            {
              key: "globalActivityConfig.refreshConfig.antiFlicker",
              label: "防闪烁",
              enable: false,
              value: 10,
              range: [0, 32],
              type: "number",
            },
            {
              key: "globalActivityConfig.refreshConfig.gcInterval",
              label: "按点击次数全刷",
              enable: false,
              value: 20,
              range: [0, 50],
              type: "number",
            },
            {
              key: "globalActivityConfig.refreshConfig.useGCForNewSurface",
              label: "页面切换时全刷",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "globalActivityConfig.refreshConfig.refreshModeIndex",
              label: "刷新模式",
              enable: false,
              value: "refresh_mode_2",
              range: [
                { label: "净清", value: "refresh_mode_1" },
                { label: "标准", value: "refresh_mode_2" },
                { label: "极速", value: "refresh_mode_3" },
              ],
              type: "select",
            },
          ],
        },
        {
          groupTitle: "网页配置",
          items: [
            {
              key: "globalCSSConfig.fontBold",
              label: "网页文字加粗",
              enable: false,
              type: "boolean",
              value: false,
            },
            {
              key: "globalCSSConfig.fontColor",
              label: "网页文字颜色",
              enable: false,
              value: 0,
              range: [0, 255],
              type: "number",
            },
            {
              key: "globalCSSConfig.fontSize",
              label: "网页字号",
              enable: false,
              value: 100,
              range: [100, 120],
              type: "number",
            },
          ],
        },
        {
          groupTitle: "键盘与旋转",
          items: [
            {
              key: "keyboardConfig.pageKeyMode",
              label: "侧键定义",
              enable: false,
              value: 0,
              range: [
                { label: "翻页", value: 0 },
                { label: "音量", value: 1 },
                { label: "滚屏手势", value: 2 },
              ],
              type: "select",
            },
            {
              key: "rotationConfig.enable",
              label: "强制旋转",
              enable: false,
              type: "boolean",
              value: false,
            },
          ],
        },
        {
          groupTitle: "滚动配置",
          items: [
            {
              key: "scrollArgs.endYPercent",
              label: "结束位置",
              enable: false,
              value: 0.8,
              range: [0, 1],
              step: 0.01,
              type: "number",
            },
            {
              key: "scrollArgs.startYPercent",
              label: "起始位置",
              enable: false,
              value: 0.2,
              range: [0, 1],
              step: 0.01,
              type: "number",
            },
          ],
        },
      ],
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== this.jsonString) {
          this.loadConfigFromJson(newVal);
        }
      },
    },
  },
  methods: {
    // 从 JSON 字符串加载配置到可视化编辑器
    loadConfigFromJson(jsonStr) {
      if (!jsonStr) {
        this.jsonString = "";
        this.resetAllItems();
        return;
      }

      try {
        this.jsonString = jsonStr;
        const config = JSON.parse(jsonStr);

        // 重置所有配置项
        this.resetAllItems();

        // 遍历配置项并设置值
        for (const group of this.configForm) {
          for (const item of group.items) {
            const value = this.getNestedProperty(config, item.key);
            if (value !== undefined) {
              item.enable = true;
              item.value = value;
            }
          }
        }
      } catch (error) {
        console.error("解析配置失败:", error);
      }
    },

    // 重置所有配置项
    resetAllItems() {
      for (const group of this.configForm) {
        for (const item of group.items) {
          item.enable = false;
        }
      }
    },

    // 获取嵌套属性
    getNestedProperty(obj, path) {
      const keys = path.split(".");
      let result = obj;
      for (const key of keys) {
        if (result && result.hasOwnProperty(key)) {
          result = result[key];
        } else {
          return undefined;
        }
      }
      return result;
    },

    // 设置嵌套属性
    setNestedProperty(obj, path, value) {
      const keys = path.split(".");
      let current = obj;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key] || typeof current[key] !== "object") {
          current[key] = {};
        }
        current = current[key];
      }

      current[keys[keys.length - 1]] = value;
    },

    // 配置项启用/禁用切换
    handleItemToggle(item) {
      this.updateConfig();
    },

    // 下拉框变更
    handleSelectChange(item) {
      this.updateConfig();
    },

    // 更新配置
    updateConfig() {
      const config = {};

      for (const group of this.configForm) {
        for (const item of group.items) {
          if (item.enable) {
            this.setNestedProperty(config, item.key, item.value);
          }
        }
      }

      const jsonStr = Object.keys(config).length > 0
        ? JSON.stringify(config, null, 2)
        : "";

      this.jsonString = jsonStr;
      this.$emit("input", jsonStr);
    },

    // JSON 编辑器变更
    handleJsonChange(value) {
      this.jsonError = "";

      if (!value) {
        this.resetAllItems();
        this.$emit("input", "");
        return;
      }

      try {
        const config = JSON.parse(value);

        // 验证成功，同步到可视化编辑器
        this.loadConfigFromJson(value);
        this.$emit("input", value);
      } catch (error) {
        this.jsonError = "JSON 格式错误: " + error.message;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.config-editor {
  .editor-tabs {
    margin-bottom: 20px;
  }

  .visual-editor {
    .config-item {
      padding: 15px;
      margin-bottom: 10px;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background: #fff;
      transition: all 0.3s;

      &:hover {
        border-color: #409eff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      &.config-item-disabled {
        background: #f5f7fa;
        opacity: 0.6;
      }

      .config-item-header {
        margin-bottom: 10px;

        .config-label {
          font-weight: 500;
          font-size: 14px;
        }
      }

      .config-item-body {
        padding-left: 24px;

        .config-control {
          margin-bottom: 10px;

          ::v-deep .el-slider {
            margin: 10px 0;
          }
        }

        .config-key {
          font-size: 12px;
          color: #909399;
          margin-top: 8px;

          .key-label {
            margin-right: 5px;
          }

          code {
            background: #f5f7fa;
            padding: 2px 6px;
            border-radius: 3px;
            font-family: "Courier New", monospace;
            color: #e6a23c;
          }
        }
      }
    }
  }

  .json-editor {
    .el-textarea {
      ::v-deep textarea {
        font-family: "Courier New", monospace;
        font-size: 13px;
      }
    }

    .json-error {
      color: #f56c6c;
      font-size: 12px;
      margin-top: 8px;
      padding: 8px;
      background: #fef0f0;
      border-radius: 4px;
    }
  }
}

::v-deep .el-collapse-item__header {
  font-weight: 600;
  font-size: 15px;
}
</style>
